import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Upload, RefreshCw, Edit3, Save, X, ChevronDown, ChevronUp, FileSpreadsheet, Link2, BarChart3, AlertTriangle, CheckCircle2, ArrowUpDown, ArrowLeft, Trash2, Settings, ArrowRightLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import CarryoverPanel from '../components/planning/CarryoverPanel';
import CarouselEditor from '../components/planning/CarouselEditor';

const API = process.env.REACT_APP_BACKEND_URL;

const VIEW_MODES = [
  { id: 'weekly', label: 'Weekly' },
  { id: 'daily', label: 'Daily' },
  { id: 'monthly', label: 'Monthly' },
];

// ============================
// MATERIAL MAPPINGS PANEL
// ============================
function MappingsPanel({ projectId, mappings, onRefresh }) {
  const [editingId, setEditingId] = useState(null);
  const [editKiel, setEditKiel] = useState('');

  const handleSave = async (mappingId) => {
    try {
      await axios.put(`${API}/api/mdp/mappings/${mappingId}`, { kiel_material_code: editKiel });
      toast.success('Mapping updated');
      setEditingId(null);
      onRefresh();
    } catch (err) { toast.error('Failed to update mapping'); }
  };

  if (!mappings.length) return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="mappings-panel">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
        <Link2 className="w-4 h-4 text-indigo-500" />
        <span className="text-sm font-semibold text-slate-700">Material Mappings</span>
        <span className="text-xs text-slate-400 ml-1">({mappings.length})</span>
      </div>
      <div className="max-h-[300px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 sticky top-0">
            <tr>
              <th className="text-left px-3 py-2 text-slate-500 font-medium">Customer Code</th>
              <th className="text-left px-3 py-2 text-slate-500 font-medium">Description</th>
              <th className="text-left px-3 py-2 text-slate-500 font-medium">Kiel Code</th>
              <th className="w-16 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {mappings.map(m => (
              <tr key={m.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="px-3 py-1.5 font-mono text-slate-700">{m.customer_material_code}</td>
                <td className="px-3 py-1.5 text-slate-600 truncate max-w-[200px]">{m.customer_material_description}</td>
                <td className="px-3 py-1.5">
                  {editingId === m.id ? (
                    <input value={editKiel} onChange={e => setEditKiel(e.target.value)}
                      className="w-full border border-indigo-300 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400"
                      data-testid={`mapping-kiel-input-${m.id}`} autoFocus />
                  ) : (
                    <span className={`font-mono ${m.kiel_material_code ? 'text-indigo-600' : 'text-slate-300 italic'}`}>
                      {m.kiel_material_code || 'Not mapped'}
                    </span>
                  )}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {editingId === m.id ? (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => handleSave(m.id)} className="text-emerald-500 hover:text-emerald-700" data-testid={`mapping-save-${m.id}`}>
                        <Save className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => setEditingId(null)} className="text-slate-400 hover:text-slate-600">
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => { setEditingId(m.id); setEditKiel(m.kiel_material_code || ''); }}
                      className="text-slate-400 hover:text-indigo-500" data-testid={`mapping-edit-${m.id}`}>
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================
// COCKPIT TABLE
// ============================
function CockpitTable({ cockpitData, viewMode, onEditDemand }) {
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [expandedMaterial, setExpandedMaterial] = useState(null);

  if (!cockpitData || !cockpitData.materials || cockpitData.materials.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 p-8 text-center text-slate-400 text-sm" data-testid="cockpit-empty">
        No MDP data available. Upload an Excel file to get started.
      </div>
    );
  }

  const { buckets, materials } = cockpitData;

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  let sortedMaterials = [...materials];
  if (sortField) {
    sortedMaterials.sort((a, b) => {
      let va = a.totals?.[sortField] ?? 0;
      let vb = b.totals?.[sortField] ?? 0;
      return sortDir === 'asc' ? va - vb : vb - va;
    });
  }

  // Group by type
  const seats = sortedMaterials.filter(m => m.component_type === 'seat');
  const others = sortedMaterials.filter(m => m.component_type === 'other');

  const getBucketLabel = (bucket) => {
    if (viewMode === 'weekly') {
      // "2026-W05" -> "W05"
      return bucket.includes('-W') ? bucket.split('-')[1] : bucket;
    }
    if (viewMode === 'monthly') {
      // "2026-02" -> "Feb 26"
      const parts = bucket.split('-');
      const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      return `${months[parseInt(parts[1])-1]} ${parts[0].slice(2)}`;
    }
    // daily: "2026-02-02" -> "02 Feb"
    const d = new Date(bucket + 'T00:00:00');
    return `${d.getDate().toString().padStart(2,'0')} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}`;
  };

  const getShortageColor = (val) => {
    if (val > 0) return 'text-red-600 bg-red-50 font-bold';
    return 'text-emerald-600';
  };

  const renderMaterialRow = (mat, idx) => {
    const isExpanded = expandedMaterial === mat.customer_material_code;
    const rowBg = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50';
    const sittingsLabel = mat.component_type === 'seat' && mat.sittings_per_unit > 0
      ? `${mat.sittings_per_unit}P` : null;

    return (
      <React.Fragment key={mat.customer_material_code}>
        <tr className={`${rowBg} border-t border-slate-100 hover:bg-amber-50/30 cursor-pointer transition-colors`}
          onClick={() => setExpandedMaterial(isExpanded ? null : mat.customer_material_code)}
          data-testid={`cockpit-row-${mat.customer_material_code}`}>
          <td className={`px-3 py-2 sticky left-0 ${rowBg} z-10 font-mono text-slate-700 font-medium`}>
            <div className="flex items-center gap-1.5">
              {isExpanded ? <ChevronUp className="w-3 h-3 text-slate-400" /> : <ChevronDown className="w-3 h-3 text-slate-400" />}
              <span className="truncate">{mat.customer_material_code}</span>
              {sittingsLabel && <span className="text-[9px] bg-blue-100 text-blue-600 px-1 rounded font-bold">{sittingsLabel}</span>}
            </div>
          </td>
          <td className="px-3 py-2 font-mono text-indigo-600 text-[11px]">{mat.kiel_material_code || '-'}</td>
          <td className="px-3 py-2 text-slate-600 truncate max-w-[200px]">{mat.customer_material_description}</td>
          <td className="text-center px-2 py-2 font-semibold text-slate-700">{mat.totals.mdp}</td>
          <td className="text-center px-2 py-2 text-blue-600 font-medium">{mat.totals.planned}</td>
          <td className="text-center px-2 py-2 text-amber-600 font-medium">{mat.totals.completed}</td>
          <td className="text-center px-2 py-2 text-emerald-600 font-medium">{mat.totals.shipped}</td>
          <td className={`text-center px-2 py-2 ${getShortageColor(mat.totals.shortage)}`}>
            {mat.totals.shortage}
          </td>
          <td className={`text-center px-2 py-2 ${mat.totals.planned_shortage > 0 ? 'text-orange-600 font-bold' : 'text-slate-400'}`}>
            {mat.totals.planned_shortage}
          </td>
        </tr>

        {/* Expanded: show per-bucket breakdown */}
        {isExpanded && mat.buckets && mat.buckets.length > 0 && (
          <tr>
            <td colSpan={9} className="p-0">
              <div className="bg-slate-50 border-y border-slate-200 px-4 py-2">
                <div className="overflow-x-auto">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="text-slate-500">
                        <th className="text-left px-2 py-1 font-medium">{viewMode === 'weekly' ? 'Week' : viewMode === 'monthly' ? 'Month' : 'Date'}</th>
                        <th className="text-center px-2 py-1 font-medium">MDP</th>
                        <th className="text-center px-2 py-1 font-medium">Adj.MDP</th>
                        <th className="text-center px-2 py-1 font-medium">Planned</th>
                        <th className="text-center px-2 py-1 font-medium">Produced</th>
                        <th className="text-center px-2 py-1 font-medium">Shipped</th>
                        <th className="text-center px-2 py-1 font-medium">Shortage</th>
                        <th className="text-center px-2 py-1 font-medium">Pl.Short</th>
                        <th className="text-center px-2 py-1 font-medium">Carry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mat.buckets.map((b, bi) => (
                        <tr key={b.bucket} className={`${bi % 2 === 0 ? '' : 'bg-white'} border-t border-slate-100`}>
                          <td className="px-2 py-1 font-medium text-slate-700">{getBucketLabel(b.bucket)}</td>
                          <td className="text-center px-2 py-1 text-slate-600">{b.mdp}</td>
                          <td className="text-center px-2 py-1 text-slate-500">{b.adjusted_mdp}</td>
                          <td className="text-center px-2 py-1 text-blue-600">{b.planned}</td>
                          <td className="text-center px-2 py-1 text-amber-600">{b.completed}</td>
                          <td className="text-center px-2 py-1 text-emerald-600">{b.shipped}</td>
                          <td className={`text-center px-2 py-1 ${b.shortage > 0 ? 'text-red-600 font-bold' : 'text-emerald-500'}`}>{b.shortage}</td>
                          <td className={`text-center px-2 py-1 ${b.planned_shortage > 0 ? 'text-orange-600 font-bold' : 'text-slate-400'}`}>{b.planned_shortage}</td>
                          <td className="text-center px-2 py-1 text-purple-500">{b.overproduction_carry > 0 ? `+${b.overproduction_carry}` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden" data-testid="cockpit-table">
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-slate-800 text-white">
              <th className="text-left px-3 py-2.5 sticky left-0 bg-slate-800 z-10 min-w-[120px]">Customer Code</th>
              <th className="text-left px-3 py-2.5 min-w-[100px]">Kiel Code</th>
              <th className="text-left px-3 py-2.5 min-w-[150px]">Description</th>
              {/* Totals columns */}
              <th className="text-center px-2 py-2.5 cursor-pointer hover:bg-slate-700 min-w-[60px]" onClick={() => toggleSort('mdp')}>
                <div className="flex items-center justify-center gap-1">MDP <ArrowUpDown className="w-3 h-3 opacity-50" /></div>
              </th>
              <th className="text-center px-2 py-2.5 cursor-pointer hover:bg-slate-700 min-w-[60px]" onClick={() => toggleSort('planned')}>
                <div className="flex items-center justify-center gap-1">Planned <ArrowUpDown className="w-3 h-3 opacity-50" /></div>
              </th>
              <th className="text-center px-2 py-2.5 cursor-pointer hover:bg-slate-700 min-w-[60px]" onClick={() => toggleSort('completed')}>
                <div className="flex items-center justify-center gap-1">Produced <ArrowUpDown className="w-3 h-3 opacity-50" /></div>
              </th>
              <th className="text-center px-2 py-2.5 cursor-pointer hover:bg-slate-700 min-w-[60px]" onClick={() => toggleSort('shipped')}>
                <div className="flex items-center justify-center gap-1">Shipped <ArrowUpDown className="w-3 h-3 opacity-50" /></div>
              </th>
              <th className="text-center px-2 py-2.5 cursor-pointer hover:bg-slate-700 min-w-[60px]" onClick={() => toggleSort('shortage')}>
                <div className="flex items-center justify-center gap-1">Shortage <ArrowUpDown className="w-3 h-3 opacity-50" /></div>
              </th>
              <th className="text-center px-2 py-2.5 cursor-pointer hover:bg-slate-700 min-w-[70px]" onClick={() => toggleSort('planned_shortage')}>
                <div className="flex items-center justify-center gap-1">Pl.Short <ArrowUpDown className="w-3 h-3 opacity-50" /></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {/* SEATS GROUP */}
            {seats.length > 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-1.5 bg-blue-50 border-t border-blue-100">
                  <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider">
                    Seats ({seats.length} types) - {cockpitData.summary?.total_seat_sittings?.toLocaleString() || 0} total seat placed (MDP)
                  </span>
                </td>
              </tr>
            )}
            {seats.map((mat, idx) => renderMaterialRow(mat, idx))}

            {/* OTHERS GROUP */}
            {others.length > 0 && (
              <tr>
                <td colSpan={9} className="px-3 py-1.5 bg-slate-100 border-t border-slate-200">
                  <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Other Components ({others.length})
                  </span>
                </td>
              </tr>
            )}
            {others.map((mat, idx) => renderMaterialRow(mat, idx + seats.length))}

            {/* Summary row */}
            <tr className="bg-slate-800 text-white font-semibold">
              <td className="px-3 py-2.5 sticky left-0 bg-slate-800 z-10" colSpan={3}>
                TOTAL ({cockpitData.summary?.total_materials || 0} materials)
              </td>
              <td className="text-center px-2 py-2.5">{cockpitData.summary?.total_mdp || 0}</td>
              <td className="text-center px-2 py-2.5">{cockpitData.summary?.total_planned || 0}</td>
              <td className="text-center px-2 py-2.5">{cockpitData.summary?.total_completed || 0}</td>
              <td className="text-center px-2 py-2.5">{cockpitData.summary?.total_shipped || 0}</td>
              <td className="text-center px-2 py-2.5 text-red-300">
                {cockpitData.summary?.total_shortage || 0}
              </td>
              <td className="text-center px-2 py-2.5 text-orange-300">
                {cockpitData.summary?.total_planned_shortage || 0}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ============================
// MAIN PAGE COMPONENT
// ============================
export default function MdpCockpit({ projectId: propProjectId, projectName: propProjectName, onBack }) {
  const [selectedProject, setSelectedProject] = useState(propProjectId || null);
  const [viewMode, setViewMode] = useState('weekly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [cockpitData, setCockpitData] = useState(null);
  const [mappings, setMappings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMappings, setShowMappings] = useState(false);
  const [showClearMenu, setShowClearMenu] = useState(false);
  const [showCarryover, setShowCarryover] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const fileRef = useRef(null);
  const clearMenuRef = useRef(null);

  // Close clear menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (clearMenuRef.current && !clearMenuRef.current.contains(e.target)) setShowClearMenu(false);
    };
    if (showClearMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showClearMenu]);

  const handleClear = async (year) => {
    const label = year ? `${year} MDP verisi` : 'TÜM MDP verisi';
    if (!window.confirm(`${label} silinecek. Emin misiniz?`)) return;
    try {
      const params = year ? `project_id=${selectedProject}&year=${year}` : `project_id=${selectedProject}`;
      await axios.delete(`${API}/api/mdp/clear?${params}`);
      toast.success(`${label} cleared`);
      setShowClearMenu(false);
      fetchCockpit();
    } catch (err) { toast.error('Clear failed'); }
  };

  // Set project from props
  useEffect(() => {
    if (propProjectId) setSelectedProject(propProjectId);
  }, [propProjectId]);

  // Fetch available years
  useEffect(() => {
    if (!selectedProject) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/api/mdp/years?project_id=${selectedProject}`);
        const years = res.data || [];
        setAvailableYears(years);
        if (years.length > 0 && !years.includes(selectedYear)) {
          setSelectedYear(years[0]);
        }
      } catch (err) { console.error(err); }
    })();
  }, [selectedProject]);

  // Fetch cockpit data when project, view or year changes
  const fetchCockpit = useCallback(async () => {
    if (!selectedProject) return;
    setLoading(true);
    try {
      const [cockpitRes, mappingsRes, summaryRes] = await Promise.all([
        axios.get(`${API}/api/mdp/cockpit?project_id=${selectedProject}&view=${viewMode}&year=${selectedYear}`),
        axios.get(`${API}/api/mdp/mappings?project_id=${selectedProject}`),
        axios.get(`${API}/api/mdp/summary?project_id=${selectedProject}&year=${selectedYear}`),
      ]);
      setCockpitData(cockpitRes.data);
      setMappings(mappingsRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Failed to fetch cockpit', err);
      toast.error('Failed to load cockpit data');
    } finally { setLoading(false); }
  }, [selectedProject, viewMode, selectedYear]);

  useEffect(() => { fetchCockpit(); }, [fetchCockpit]);

  // Upload Excel
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProject) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', selectedProject);

    try {
      const res = await axios.post(`${API}/api/mdp/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`${res.data.message}`);
      fetchCockpit();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleUploadDone = () => {
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-4" data-testid="mdp-cockpit-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {onBack && (
            <button onClick={onBack}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors" data-testid="mdp-back-btn">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </button>
          )}
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-800" data-testid="mdp-cockpit-title">
              MDP Planning Cockpit
              {propProjectName && <span className="text-amber-600 ml-2">- {propProjectName}</span>}
            </h1>
            <p className="text-xs text-slate-400">Customer Demand vs Production Planning</p>
          </div>
        </div>
      </div>

      {/* Controls Bar */}
      <div className="flex flex-wrap items-center gap-3 bg-white rounded-lg border border-slate-200 p-3">
        {/* View Mode */}
        <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5">
          {VIEW_MODES.map(v => (
            <button key={v.id} onClick={() => setViewMode(v.id)}
              className={`px-3 py-1 text-xs font-medium rounded transition-all ${viewMode === v.id ? 'bg-amber-500 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`view-mode-${v.id}`}>
              {v.label}
            </button>
          ))}
        </div>

        {/* Year Selector */}
        {availableYears.length > 0 && (
          <div className="flex items-center gap-1 bg-slate-100 rounded-md p-0.5">
            {availableYears.map(y => (
              <button key={y} onClick={() => setSelectedYear(y)}
                className={`px-3 py-1 text-xs font-medium rounded transition-all ${selectedYear === y ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
                data-testid={`year-${y}`}>
                {y}
              </button>
            ))}
          </div>
        )}

        <div className="flex-1" />

        {/* Upload Button */}
        <input type="file" ref={fileRef} accept=".xlsx,.xls" onChange={handleUpload} className="hidden" data-testid="mdp-file-input" />
        <button onClick={() => fileRef.current?.click()} disabled={!selectedProject || uploading}
          className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          data-testid="mdp-upload-btn">
          {uploading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {uploading ? 'Uploading...' : 'Upload MDP Excel'}
        </button>

        {/* Toggle Mappings */}
        <button onClick={() => setShowMappings(!showMappings)}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${showMappings ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="toggle-mappings-btn">
          <Link2 className="w-3.5 h-3.5" />
          Mappings
        </button>

        {/* Carry-over */}
        <button onClick={() => { setShowCarryover(!showCarryover); setShowCarousel(false); }}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${showCarryover ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="toggle-carryover-btn">
          <ArrowRightLeft className="w-3.5 h-3.5" />
          Carry-over
        </button>

        {/* Carousel Editor */}
        <button onClick={() => { setShowCarousel(!showCarousel); setShowCarryover(false); }}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${showCarousel ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}
          data-testid="toggle-carousel-btn">
          <Settings className="w-3.5 h-3.5" />
          Carousel
        </button>

        {/* Refresh */}
        <button onClick={fetchCockpit} className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 text-slate-600 rounded-md text-xs font-medium hover:bg-slate-200 transition-colors"
          data-testid="mdp-refresh-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>

        {/* Clear MDP */}
        <div className="relative" ref={clearMenuRef}>
          <button onClick={() => setShowClearMenu(!showClearMenu)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-600 rounded-md text-xs font-medium hover:bg-red-100 transition-colors"
            data-testid="mdp-clear-btn">
            <Trash2 className="w-3.5 h-3.5" />
            Clear
          </button>
          {showClearMenu && (
            <div className="absolute right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-xl border border-slate-200 py-1 w-48">
              <button onClick={() => handleClear(selectedYear)}
                className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition-colors"
                data-testid="clear-year-btn">
                Clear {selectedYear} only
              </button>
              <button onClick={() => handleClear(null)}
                className="w-full text-left px-3 py-2 text-xs text-red-700 font-semibold hover:bg-red-50 transition-colors border-t border-slate-100"
                data-testid="clear-all-btn">
                Clear ALL years
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Summary Cards - Improved */}
      {summary && summary.record_count > 0 && cockpitData?.materials && (
        <div className="space-y-3" data-testid="mdp-summary-cards">
          {/* Seat Classification */}
          {(() => {
            const mats = cockpitData.materials || [];
            const seats2P = mats.filter(m => m.component_type === 'seat' && m.sittings_per_unit === 2);
            const seats1P = mats.filter(m => m.component_type === 'seat' && m.sittings_per_unit === 1);
            const otherMats = mats.filter(m => m.component_type !== 'seat');
            const totalSeatPlaced = mats.filter(m => m.component_type === 'seat').reduce((s, m) => s + (m.totals.mdp * (m.sittings_per_unit || 1)), 0);
            const totalSeats = mats.filter(m => m.component_type === 'seat').reduce((s, m) => s + m.totals.mdp, 0);
            const totalOtherDemand = otherMats.reduce((s, m) => s + m.totals.mdp, 0);
            const totalPlanned = cockpitData.summary?.total_planned || 0;
            const totalCompleted = cockpitData.summary?.total_completed || 0;
            const totalShipped = cockpitData.summary?.total_shipped || 0;
            const totalShortage = mats.filter(m => m.component_type === 'seat').reduce((s, m) => s + Math.max(0, m.totals.mdp - m.totals.shipped), 0);

            return (
              <>
                {/* Row 1: Seat Types + Others */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-blue-400 tracking-wider">2P Seats</div>
                    <div className="text-xl font-bold text-blue-700 mt-1">{seats2P.length} <span className="text-xs font-normal text-blue-400">types</span></div>
                    <div className="text-xs text-blue-500 mt-0.5">{seats2P.reduce((s,m) => s + m.totals.mdp, 0).toLocaleString()} seats / {seats2P.reduce((s,m) => s + m.totals.mdp * 2, 0).toLocaleString()} seat pl.</div>
                  </div>
                  <div className="bg-violet-50 border border-violet-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-violet-400 tracking-wider">1P Seats</div>
                    <div className="text-xl font-bold text-violet-700 mt-1">{seats1P.length} <span className="text-xs font-normal text-violet-400">types</span></div>
                    <div className="text-xs text-violet-500 mt-0.5">{seats1P.reduce((s,m) => s + m.totals.mdp, 0).toLocaleString()} seats / {seats1P.reduce((s,m) => s + m.totals.mdp * 1, 0).toLocaleString()} seat pl.</div>
                  </div>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-slate-400 tracking-wider">Other Components</div>
                    <div className="text-xl font-bold text-slate-700 mt-1">{otherMats.length} <span className="text-xs font-normal text-slate-400">types</span></div>
                    <div className="text-xs text-slate-500 mt-0.5">{totalOtherDemand.toLocaleString()} total demand</div>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-amber-400 tracking-wider">Total Seat Placed</div>
                    <div className="text-xl font-bold text-amber-700 mt-1">{totalSeatPlaced.toLocaleString()}</div>
                    <div className="text-xs text-amber-500 mt-0.5">{totalSeats.toLocaleString()} seats</div>
                  </div>
                </div>
                {/* Row 2: Production KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-orange-400 tracking-wider">Total Planned</div>
                    <div className="text-xl font-bold text-orange-600 mt-1">{totalPlanned.toLocaleString()}</div>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-emerald-400 tracking-wider">Total Produced</div>
                    <div className="text-xl font-bold text-emerald-600 mt-1">{totalCompleted.toLocaleString()}</div>
                  </div>
                  <div className="bg-teal-50 border border-teal-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-teal-400 tracking-wider">Total Shipped</div>
                    <div className="text-xl font-bold text-teal-600 mt-1">{totalShipped.toLocaleString()}</div>
                  </div>
                  <div className="bg-red-50 border border-red-100 rounded-lg p-3">
                    <div className="text-[9px] font-bold uppercase text-red-400 tracking-wider">Total Shortage</div>
                    <div className="text-xl font-bold text-red-600 mt-1">{totalShortage.toLocaleString()}</div>
                    <div className="text-xs text-red-400 mt-0.5">seats</div>
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Mappings Panel (collapsible) */}
      {showMappings && (
        <MappingsPanel projectId={selectedProject} mappings={mappings} onRefresh={fetchCockpit} />
      )}

      {/* Carry-over Panel */}
      {showCarryover && selectedProject && (
        <CarryoverPanel projectId={selectedProject} onClose={() => setShowCarryover(false)} />
      )}

      {/* Carousel Editor Panel */}
      {showCarousel && (
        <CarouselEditor onClose={() => setShowCarousel(false)} />
      )}

      {/* Main Cockpit Table */}
      {loading ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 flex items-center justify-center">
          <RefreshCw className="w-6 h-6 text-amber-500 animate-spin" />
          <span className="ml-3 text-sm text-slate-400">Loading cockpit data...</span>
        </div>
      ) : (
        <CockpitTable cockpitData={cockpitData} viewMode={viewMode} />
      )}

      {/* Info Panel */}
      {!summary || summary.record_count === 0 ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-700" data-testid="mdp-info-panel">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold">No MDP data uploaded yet</p>
              <p className="text-xs mt-1 text-amber-600">
                Select a project and upload an MDP Excel file to see the planning cockpit.
                The Excel file should contain customer material codes in Column B, delivery dates in Column H, and quantities in Column K.
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// Summary card component
function SummaryCard({ label, value, icon, color }) {
  const colorMap = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    indigo: 'bg-indigo-50 text-indigo-600 border-indigo-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
    emerald: 'bg-emerald-50 text-emerald-600 border-emerald-100',
    red: 'bg-red-50 text-red-600 border-red-100',
    slate: 'bg-slate-50 text-slate-600 border-slate-100',
  };

  return (
    <div className={`rounded-lg border p-3 ${colorMap[color] || colorMap.slate}`} data-testid={`summary-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <span className="text-[10px] font-medium uppercase tracking-wide opacity-70">{label}</span>
      </div>
      <div className="text-lg font-bold">{typeof value === 'number' ? value.toLocaleString() : value}</div>
    </div>
  );
}
